// src/web/main.tsx

import { QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'

import { queryClient } from './lib/query'
import { setRouterInstance } from './lib/router'
import { routeTree } from './routeTree.gen'
import { useWorkspaceStore } from './stores/workspace'

import './styles/globals.css'

// Create the router
const router = createRouter({
	routeTree,
	context: {
		queryClient,
	},
	defaultPreload: 'intent',
})

// Expose router for non-React SPA navigation (e.g. toast click handlers)
setRouterInstance(router)

// Register the router for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}

// Render the app
const rootElement = document.getElementById('root')
const posthogEnabled = import.meta.env.VITE_PUBLIC_POSTHOG_ENABLED === 'true'
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY

if (rootElement && !rootElement.innerHTML) {
	const root = createRoot(rootElement)
	root.render(
		<StrictMode>
			{posthogEnabled && posthogKey ? (
				<PostHogProvider
					apiKey={posthogKey}
					options={{
						api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
						defaults: '2026-01-30',
						// Error tracking: frontend exception autocapture
						capture_exceptions: true,
						// Performance: web vitals (LCP/CLS/FCP/INP) + network timing
						capture_performance: { web_vitals: true, network_timing: true },
						// Link browser sessions to API traces/exceptions via
						// X-POSTHOG-SESSION-ID / X-POSTHOG-DISTINCT-ID headers
						tracing_headers: [window.location.hostname],
						// PostHog Logs: browser console output, filterable by
						// service.name=dam-web (API logs use dam-api)
						logs: {
							serviceName: 'dam-web',
							environment: import.meta.env.MODE,
							captureConsoleLogs: true,
						},
						debug: import.meta.env.MODE === 'development',
					}}
				>
					<PostHogInitializer>
						<QueryClientProvider client={queryClient}>
							<RouterProvider router={router} />
						</QueryClientProvider>
					</PostHogInitializer>
				</PostHogProvider>
			) : (
				<QueryClientProvider client={queryClient}>
					<RouterProvider router={router} />
				</QueryClientProvider>
			)}
		</StrictMode>
	)
}

function PostHogInitializer({ children }: { children: React.ReactNode }) {
	const currentWorkspace = useWorkspaceStore((state) => state.currentWorkspace)

	useEffect(() => {
		// Super properties on every frontend event; `service` discriminates
		// dam-web from the server-mirrored dam-api events when filtering
		posthog.register({
			service: 'dam-web',
			environment: import.meta.env.MODE,
		})

		// npm-bundled posthog-js doesn't attach to window (only the snippet
		// does) — expose it in dev for console debugging
		if (import.meta.env.MODE === 'development') {
			;(window as unknown as { posthog: typeof posthog }).posthog = posthog
		}
	}, [])

	useEffect(() => {
		// Stamp workspace id + name on all frontend events and tie the session
		// to the `workspace` group (matches the server mirror's $groups)
		if (currentWorkspace) {
			posthog.register({
				workspace_id: currentWorkspace.id,
				workspace_name: currentWorkspace.name,
			})
			posthog.group('workspace', currentWorkspace.id, { name: currentWorkspace.name })
		}
	}, [currentWorkspace])

	return <>{children}</>
}
